// Page scaffolding shared by every page: header, empty, loading, error and no-access states.
import { CircleAlert, Inbox, LoaderCircle, Lock, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { type ApiError, isApiError } from '../lib/api'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Skeleton } from './ui/misc'

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('mb-5', className)}>
      {breadcrumb ? <div className="mb-2 text-[13px] text-ink-subtle">{breadcrumb}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-[22px] font-semibold tracking-tight text-ink">{title}</h1>
          {description ? <div className="mt-1 text-sm text-ink-muted">{description}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      <div className="mb-3 grid size-11 place-items-center rounded-full bg-brand-50 text-brand-500 [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden />}
      </div>
      <p className="text-sm font-semibold text-ink">{title}</p>
      {description ? <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const apiErr: ApiError | null = isApiError(error) ? error : null
  if (apiErr?.isForbidden) {
    return (
      <EmptyState
        className={className}
        icon={<Lock aria-hidden />}
        title="You do not have access to this page"
        description={`${apiErr.message.replace(/[.\s]+$/, '')}. Ask a Clearpol admin if you need access.`}
      />
    )
  }
  if (apiErr?.isNotFound) {
    return (
      <EmptyState
        className={className}
        icon={<CircleAlert aria-hidden />}
        title="Not found"
        description={apiErr.message}
      />
    )
  }
  const message = error instanceof Error ? error.message : 'Something went wrong.'
  return (
    <EmptyState
      className={className}
      icon={<CircleAlert aria-hidden />}
      title="This page could not load"
      description={message}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            <RefreshCw aria-hidden />
            Try again
          </Button>
        ) : undefined
      }
    />
  )
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-sm text-ink-subtle', className)}>
      <LoaderCircle className="size-4 animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function FullPageSpinner({ label = 'Loading Clearpol Compliance' }: { label?: string }) {
  return (
    <div className="grid min-h-full place-items-center bg-canvas p-10">
      <div className="flex flex-col items-center gap-3 text-ink-subtle">
        <LoaderCircle className="size-6 animate-spin text-brand-500" aria-hidden />
        <p className="text-sm">{label}</p>
      </div>
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

export function NoAccess({ what }: { what: string }) {
  return (
    <EmptyState
      icon={<Lock aria-hidden />}
      title="You do not have access to this page"
      description={`Your role does not include ${what}. Ask a Clearpol admin if you need access.`}
    />
  )
}
